import { getDefaultTonic, getModeLabOptions, type ModeLabMode } from '@tunasong/music-lib'
import type { NoteName } from '@tunasong/schemas'
import type { FC } from 'react'
import { useState } from 'react'
import { useTheoryLevel, type TheoryLevel } from '../theory-copy/use-theory-copy.js'
import { ModeCharacteristicChord } from './mode-characteristic-chord.js'
import { ModePicker } from './mode-picker.js'
import { ModeScaleDisplay } from './mode-scale-display.js'
import { useModeLabPlayback } from './mode-lab-playback.js'
import styles from './mode-lab.module.css'

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

/**
 * §8.6 per-level register table for the parallel-reference name. The tonic
 * is supplied by the caller so the resolver doesn't need to know about
 * spelling.
 *
 *  - Beginner: "minor" / "major" (drop "natural", drop mode-name pedantry)
 *  - Intermediate: "natural minor" / "major"
 *  - Advanced: "Aeolian" / "Ionian"
 *
 * `'off'` falls back to the intermediate text — Mode Lab is itself a
 * teaching surface so we still need *some* label even when chips are off.
 */
const parallelReferenceName = (
  parallelType: 'major' | 'minor',
  level: TheoryLevel
): string => {
  if (level === 'beginner') {
    return parallelType === 'minor' ? 'minor' : 'major'
  }
  if (level === 'advanced') {
    return parallelType === 'minor' ? 'Aeolian' : 'Ionian'
  }
  // 'intermediate' and 'off' both use the intermediate register
  return parallelType === 'minor' ? 'natural minor' : 'major'
}

/**
 * Mode Lab v1 host. Owns mode + tonic + comparing state in local React
 * state; no persistence (§2 Q6). Spec: docs/superpowers/specs/2026-05-18-mode-lab-design.md
 *
 * Bowie §8 verdicts wired in:
 *  - §8.1/§8.2 — parallel vamps encoded in `mode-lab-options.ts`
 *  - §8.3 — characteristic chord is the major triad named in the locked table
 *  - §8.4 — enharmonic spelling deferred to `ScaleLib.getNotes`
 *  - §8.5 — switching mode resets tonic to canonical default (no per-mode memory)
 *  - §8.6 — Compare-toggle label resolves per theoryLevel
 */
export const ModeLab: FC = () => {
  const [mode, setMode] = useState<ModeLabMode>('dorian')
  const [tonic, setTonic] = useState<NoteName>(getDefaultTonic('dorian'))
  const [comparing, setComparing] = useState(false)
  const level = useTheoryLevel()

  const options = getModeLabOptions(mode, tonic)

  const scale = comparing ? options.parallelScale : options.modeScale
  const captionLexiconId = comparing ? 'mode-characteristic-note' : options.modeLexiconId
  const vamp = comparing ? options.parallelVamp : options.modeVamp

  const { play, stop, isPlaying } = useModeLabPlayback({ vamp, loop: true })

  const parallelType: 'major' | 'minor' =
    options.parallelScale.type === 'major' ? 'major' : 'minor'
  const compareLabel = comparing
    ? `Back to ${tonic} ${capitalize(mode)}`
    : `Compare to ${tonic} ${parallelReferenceName(parallelType, level)}`

  const handlePickerChange = (nextMode: ModeLabMode, nextTonic: NoteName) => {
    stop()
    setMode(nextMode)
    setTonic(nextTonic)
    setComparing(false)
  }
  const handleToggleCompare = () => {
    stop()
    setComparing(c => !c)
  }
  const handlePlayClick = () => {
    if (isPlaying) {
      stop()
    } else {
      play()
    }
  }

  return (
    <div className={styles.lab}>
      <ModePicker mode={mode} tonic={tonic} onChange={handlePickerChange} />
      <ModeScaleDisplay
        scale={scale}
        characteristicDegreeIndex={options.characteristicDegreeIndex}
        captionLexiconId={captionLexiconId}
      />
      {!comparing ? (
        <ModeCharacteristicChord
          chord={options.characteristicChord}
          modeLexiconId={options.modeLexiconId}
        />
      ) : null}
      <div className={styles.controls}>
        <button type="button" className={styles.buttonPrimary} onClick={handlePlayClick}>
          {isPlaying ? 'Stop' : 'Play vamp'}
        </button>
        <button type="button" className={styles.button} onClick={handleToggleCompare}>
          {compareLabel}
        </button>
      </div>
    </div>
  )
}
